import React, { useState, useEffect, useRef } from 'react'
import {
	MessageCircle,
	X,
	Send,
	User,
	Phone,
	Mail,
	MessageSquare,
	Headphones,
	Clock,
	CheckCircle2,
	AlertCircle,
} from 'lucide-react'
import * as Yup from 'yup'
import { ContactEntity, Status } from '@/entities/api/contact/contact'
import { createContact } from '@/services/api/contact/contact'

const contactSchema = Yup.object({
	fullname: Yup.string()
		.required('El nombre completo es requerido')
		.min(2, 'El nombre debe tener al menos 2 caracteres')
		.max(50, 'El nombre no puede exceder 50 caracteres'),
	phone: Yup.number()
		.required('El teléfono es requerido')
		.positive('Ingrese un número válido')
		.integer('Ingrese un número válido'),
	email: Yup.string()
		.required('El email es requerido')
		.email('Ingrese un email válido'),
	message: Yup.string()
		.required('El mensaje es requerido')
		.min(10, 'El mensaje debe tener al menos 10 caracteres')
		.max(500, 'El mensaje no puede exceder 500 caracteres'),
})

const FloatingSupportChat: React.FC = () => {
	const [isOpen, setIsOpen] = useState(false)
	const [currentStep, setCurrentStep] = useState<'chat' | 'form'>('chat')
	const [formData, setFormData] = useState({
		fullname: '',
		phone: '',
		email: '',
		message: '',
	})
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)
	const [showError, setShowError] = useState(false)
	const [isVisible, setIsVisible] = useState(false)
	const chatRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		setIsVisible(true)
	}, [])

	useEffect(() => {
		if (showSuccess || showError) {
			const timer = setTimeout(() => {
				setShowSuccess(false)
				setShowError(false)
			}, 10000)
			return () => clearTimeout(timer)
		}
	}, [showSuccess, showError])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (chatRef.current && !chatRef.current.contains(event.target as Node)) {
				if (isOpen && !isSubmitting) {
					setIsOpen(false)
				}
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen, isSubmitting])

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target
		setFormData((prev) => ({ ...prev, [name]: value }))

		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: '' }))
		}
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsSubmitting(true)
		setErrors({})

		try {
			await contactSchema.validate(formData, { abortEarly: false })

			const contactEntity: ContactEntity = {
				status: Status.pendding,
				fullname: formData.fullname,
				phone: Number(formData.phone),
				email: formData.email,
				message: formData.message,
			}

			const response = await createContact(contactEntity)

			if (response?.status && response.status >= 201) {
				setShowSuccess(true)
				setFormData({ fullname: '', phone: '', email: '', message: '' })
				setErrors({})
				setTimeout(() => {
					setCurrentStep('chat')
				}, 2000)
			} else {
				setShowError(true)
			}
		} catch (error: any) {
			if (error.inner) {
				const validationErrors: Record<string, string> = {}
				error.inner.forEach((err: any) => {
					validationErrors[err.path] = err.message
				})
				setErrors(validationErrors)
			} else {
				setShowError(true)
			}
		} finally {
			setIsSubmitting(false)
		}
	}

	const quickActions = [
		{
			icon: MessageSquare,
			title: 'Enviar Mensaje',
			description: 'Escríbenos tu consulta',
			action: () => setCurrentStep('form'),
			color: 'from-blue-500 to-blue-600',
		},
		{
			icon: Phone,
			title: 'Llamar Ahora',
			description: '+57 3225432580',
			action: () => window.open('tel:+573225432580'),
			color: 'from-green-500 to-green-600',
		},
		{
			icon: Mail,
			title: 'Email Directo',
			description: 'sportcenterchinchinabusiness@gmail.com',
			action: () => window.open('mailto:sportcenterchinchinabusiness@gmail.com'),
			color: 'from-purple-500 to-purple-600',
		},
	]

	return (
		<div
			className={`fixed bottom-32 right-4 sm:bottom-32 sm:right-4 lg:bottom-6 lg:right-6 z-[9999] transform transition-all duration-500 ease-out ${
				isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
			}`}
		>
			{isOpen && (
				<div
					ref={chatRef}
					className={`absolute bottom-16 sm:bottom-20 right-0 w-80 sm:w-96 max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)] transform transition-all duration-300 ease-out ${
						isOpen
							? 'translate-y-0 opacity-100 scale-100'
							: 'translate-y-4 opacity-0 scale-95'
					}`}
				>
					<div className='bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden'>
						<div className='bg-gradient-to-r from-red-600 to-red-700 px-4 sm:px-6 py-3 sm:py-4 relative overflow-hidden'>
							<div className='absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full -translate-y-12 sm:-translate-y-16 translate-x-12 sm:translate-x-16'></div>
							<div className='relative z-10 flex items-center justify-between'>
								<div className='flex items-center gap-2 sm:gap-3'>
									<div className='w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center'>
										<Headphones className='w-4 h-4 sm:w-5 sm:h-5 text-white' />
									</div>
									<div>
										<h3 className='text-white font-black text-base sm:text-lg tracking-tight'>
											Soporte SportCenter Shop
										</h3>
										<div className='flex items-center gap-2'>
											<div className='w-2 h-2 bg-green-400 rounded-full animate-pulse'></div>
											<span className='text-white/90 text-xs font-medium'>
												En línea
											</span>
										</div>
									</div>
								</div>
								<button
									onClick={() => setIsOpen(false)}
									className='w-7 h-7 sm:w-8 sm:h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-200'
								>
									<X className='w-4 h-4 text-white' />
								</button>
							</div>
						</div>

						<div className='p-4 sm:p-6 max-h-[70vh] sm:max-h-[32rem] overflow-y-auto'>
							{currentStep === 'chat' && (
								<div className='space-y-3 sm:space-y-4'>
									<div className='flex items-start gap-2 sm:gap-3'>
										<div className='w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1'>
											<User className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
										</div>
										<div className='bg-gray-50 rounded-2xl rounded-tl-md p-3 sm:p-4 max-w-[85%]'>
											<p className='text-gray-800 text-xs sm:text-sm font-medium mb-2'>
												¡Hola! 👋 Soy el asistente de SportCenter Shop
											</p>
											<p className='text-gray-600 text-xs sm:text-sm leading-relaxed'>
												¿En qué te puedo ayudar hoy? Elige una opción o
												escríbenos directamente.
											</p>
										</div>
									</div>

									<div className='flex justify-center'>
										<div className='bg-gray-100 px-2 sm:px-3 py-1 rounded-full'>
											<div className='flex items-center gap-1 text-gray-500 text-xs'>
												<Clock className='w-3 h-3' />
												<span>
													{new Date().toLocaleTimeString('es-ES', {
														hour: '2-digit',
														minute: '2-digit',
													})}
												</span>
											</div>
										</div>
									</div>

									<div className='space-y-2 sm:space-y-3'>
										{quickActions.map((action, index) => (
											<button
												key={index}
												onClick={action.action}
												className='w-full cursor-pointer bg-white border-2 border-gray-100 hover:border-red-200 hover:bg-red-50/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg group'
											>
												<div className='flex items-center gap-3 sm:gap-4'>
													<div
														className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${action.color} rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}
													>
														<action.icon className='w-5 h-5 sm:w-6 sm:h-6 text-white' />
													</div>
													<div className='text-left flex-1 min-w-0'>
														<h4 className='font-bold text-gray-800 text-xs sm:text-sm mb-1 truncate'>
															{action.title}
														</h4>
														<p className='text-gray-600 text-xs line-clamp-1'>
															{action.description}
														</p>
													</div>
												</div>
											</button>
										))}
									</div>

									<div className='bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 mt-4 sm:mt-6'>
										<div className='flex items-center gap-2 mb-2'>
											<Clock className='w-3 h-3 sm:w-4 sm:h-4 text-gray-500' />
											<span className='text-gray-700 font-semibold text-xs sm:text-sm'>
												Horarios de atención
											</span>
										</div>
										<p className='text-gray-600 text-xs'>
											Lunes a Domingo: 8:00 AM - 11:00 PM
										</p>
									</div>
								</div>
							)}

							{currentStep === 'form' && (
								<div className='space-y-3 sm:space-y-4'>
									<button
										onClick={() => setCurrentStep('chat')}
										className='flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors duration-200 text-xs sm:text-sm font-medium'
									>
										<MessageCircle className='w-3 h-3 sm:w-4 sm:h-4' />← Volver
										al chat
									</button>

									{showSuccess && (
										<div className='bg-green-50 border-2 border-green-200 rounded-xl sm:rounded-2xl p-3 sm:p-4'>
											<div className='flex items-center gap-2 sm:gap-3'>
												<CheckCircle2 className='w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0' />
												<div>
													<p className='text-green-800 font-semibold text-xs sm:text-sm'>
														¡Mensaje enviado exitosamente!
													</p>
													<p className='text-green-600 text-xs'>
														Te contactaremos pronto
													</p>
												</div>
											</div>
										</div>
									)}

									{showError && (
										<div className='bg-red-50 border-2 border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4'>
											<div className='flex items-center gap-2 sm:gap-3'>
												<AlertCircle className='w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0' />
												<div>
													<p className='text-red-800 font-semibold text-xs sm:text-sm'>
														Error al enviar mensaje
													</p>
													<p className='text-red-600 text-xs'>
														Inténtalo nuevamente
													</p>
												</div>
											</div>
										</div>
									)}

									<form
										onSubmit={handleSubmit}
										className='space-y-3 sm:space-y-4'
									>
										<div>
											<label className='block text-xs font-bold text-gray-700 mb-1 sm:mb-2'>
												Nombre Completo *
											</label>
											<input
												type='text'
												name='fullname'
												value={formData.fullname}
												onChange={handleInputChange}
												className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border-2 rounded-lg sm:rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 bg-white ${
													errors.fullname
														? 'border-red-300 bg-red-50/50'
														: 'border-gray-200 hover:border-gray-300'
												}`}
												placeholder='Tu nombre completo'
											/>
											{errors.fullname && (
												<p className='mt-1 text-xs text-red-600 flex items-center gap-1'>
													<span className='w-1 h-1 bg-red-600 rounded-full'></span>
													{errors.fullname}
												</p>
											)}
										</div>

										<div className='grid grid-cols-2 gap-2 sm:gap-3'>
											<div>
												<label className='block text-xs font-bold text-gray-700 mb-1 sm:mb-2'>
													Teléfono *
												</label>
												<input
													type='tel'
													name='phone'
													value={formData.phone}
													onChange={handleInputChange}
													className={`w-full px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm border-2 rounded-lg sm:rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 bg-white ${
														errors.phone
															? 'border-red-300 bg-red-50/50'
															: 'border-gray-200 hover:border-gray-300'
													}`}
													placeholder='3001234567'
												/>
												{errors.phone && (
													<p className='mt-1 text-xs text-red-600 flex items-center gap-1'>
														<span className='w-1 h-1 bg-red-600 rounded-full'></span>
														{errors.phone}
													</p>
												)}
											</div>

											<div>
												<label className='block text-xs font-bold text-gray-700 mb-1 sm:mb-2'>
													Email *
												</label>
												<input
													type='email'
													name='email'
													value={formData.email}
													onChange={handleInputChange}
													className={`w-full px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm border-2 rounded-lg sm:rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 bg-white ${
														errors.email
															? 'border-red-300 bg-red-50/50'
															: 'border-gray-200 hover:border-gray-300'
													}`}
													placeholder='tu@email.com'
												/>
												{errors.email && (
													<p className='mt-1 text-xs text-red-600 flex items-center gap-1'>
														<span className='w-1 h-1 bg-red-600 rounded-full'></span>
														{errors.email}
													</p>
												)}
											</div>
										</div>

										<div>
											<label className='block text-xs font-bold text-gray-700 mb-1 sm:mb-2'>
												Mensaje *
											</label>
											<textarea
												name='message'
												value={formData.message}
												onChange={handleInputChange}
												rows={2}
												className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border-2 rounded-lg sm:rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 resize-none bg-white ${
													errors.message
														? 'border-red-300 bg-red-50/50'
														: 'border-gray-200 hover:border-gray-300'
												}`}
												placeholder='¿En qué te podemos ayudar?'
											/>
											{errors.message && (
												<p className='mt-1 text-xs text-red-600 flex items-center gap-1'>
													<span className='w-1 h-1 bg-red-600 rounded-full'></span>
													{errors.message}
												</p>
											)}
										</div>

										<button
											type='submit'
											disabled={isSubmitting}
											className='w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-red-400 disabled:to-red-500 text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 hover:scale-105 hover:shadow-xl disabled:scale-100 disabled:shadow-none group overflow-hidden relative text-xs sm:text-sm'
										>
											<div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000'></div>
											{isSubmitting ? (
												<>
													<div className='w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
													<span>Enviando...</span>
												</>
											) : (
												<>
													<Send className='w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform' />
													<span>Enviar Mensaje</span>
												</>
											)}
										</button>
									</form>
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			<button
				onClick={() => setIsOpen(!isOpen)}
				className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 cursor-pointer bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center group relative overflow-hidden ${
					isOpen ? 'scale-110 rotate-90' : 'hover:scale-110'
				}`}
			>
				<div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000'></div>
				<div className='relative z-10'>
					{isOpen ? (
						<X className='w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300' />
					) : (
						<MessageCircle className='w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 group-hover:scale-110' />
					)}
				</div>

				{!isOpen && (
					<div className='absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center'>
						<div className='w-2 h-2 bg-white rounded-full animate-pulse'></div>
					</div>
				)}
			</button>
		</div>
	)
}

export default FloatingSupportChat
